import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import MainLayout from '../../../components/layout/MainLayout';
import CountdownTimer from '../../../components/quiz/CountdownTimer';
import AnswerOptionButton from '../../../components/quiz/AnswerOptionButton';
import QuizProgressBar from '../../../components/quiz/QuizProgressBar';
import {
  getQuiz,
  startQuizAttempt,
  submitAnswer,
  completeQuizAttempt,
  getMyAttemptWithResponses,
} from '../../../services/quizService';
import { QuizWithQuestions, QuizAttempt } from '../../../types/quiz';

function QuizPlayPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const quizId = Number(id);

  const [quiz, setQuiz] = useState<QuizWithQuestions | null>(null);
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [pointsEarned, setPointsEarned] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  const loadQuiz = useCallback(async () => {
    try {
      console.log('[loadQuiz] Starting loadQuiz for quizId:', quizId);
      setLoading(true);

      console.log('[loadQuiz] Fetching quiz data...');
      const quizData = await getQuiz(quizId);
      console.log('[loadQuiz] Quiz data loaded:', quizData);
      setQuiz(quizData);

      // Use attempt from quiz response if available
      const existingAttempt = quizData.attempt;
      console.log('[loadQuiz] Existing attempt from quiz data:', existingAttempt);

      if (existingAttempt && existingAttempt.completedAt) {
        // Attempt already completed, redirect to results
        console.log('[loadQuiz] Attempt is completed, redirecting to results');
        navigate(`/student/quizzes/${quizId}/results`);
        return;
      }

      if (existingAttempt && !existingAttempt.completedAt) {
        // Continue existing attempt - fetch responses to calculate progress
        console.log('[loadQuiz] Found incomplete attempt, fetching responses...');

        const attemptWithResponses = await getMyAttemptWithResponses(quizId);
        if (attemptWithResponses) {
          setAttempt(existingAttempt);

          // Calculate which questions have been answered
          const answeredQuestionIds = new Set(
            attemptWithResponses.responses.map(r => r.questionId)
          );

          // Find the first unanswered question
          let resumeIndex = 0;
          for (let i = 0; i < quizData.questions.length; i++) {
            if (!answeredQuestionIds.has(quizData.questions[i].id)) {
              resumeIndex = i;
              break;
            }
          }

          // If all questions are answered, complete the quiz
          if (answeredQuestionIds.size === quizData.questions.length) {
            console.log('[loadQuiz] All questions answered, completing quiz...');
            await completeQuizAttempt(quizId, existingAttempt.id);
            navigate(`/student/quizzes/${quizId}/results`);
            return;
          }

          console.log(`[loadQuiz] Resuming from question ${resumeIndex + 1} of ${quizData.questions.length}`);
          setCurrentQuestionIndex(resumeIndex);

          // Initialize timer for resume question
          const resumeQuestion = quizData.questions[resumeIndex];
          if (resumeQuestion?.timeLimitSeconds) {
            setQuestionTimeRemaining(resumeQuestion.timeLimitSeconds);
          }
        }
        setLoading(false);
      } else {
        // No attempt yet - check if user can still attempt
        // Note: The quiz API should prevent this, but we check here for better UX
        console.log('[loadQuiz] No attempt found, showing start dialog');
        setShowStartDialog(true);
        setLoading(false);
      }
    } catch (err: unknown) {
      console.error('[loadQuiz] Error loading quiz:', err);
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to load quiz');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId, navigate]);

  useEffect(() => {
    loadQuiz();
  }, [loadQuiz]);

  // Reset question timer and start time when question changes
  useEffect(() => {
    if (attempt && !showFeedback && quiz) {
      const currentQuestion = quiz.questions[currentQuestionIndex];
      if (currentQuestion?.timeLimitSeconds) {
        setQuestionTimeRemaining(currentQuestion.timeLimitSeconds);
      }
      setQuestionStartTime(Date.now());
    }
  }, [currentQuestionIndex, attempt, showFeedback, quiz]);

  const handleStartQuiz = async () => {
    try {
      setLoading(true);
      const newAttempt = await startQuizAttempt(quizId);
      setAttempt(newAttempt);
      setShowStartDialog(false);
      setLoading(false);
    } catch (err: unknown) {
      console.error('Error starting quiz:', err);
      const error = err as { response?: { data?: { error?: string; message?: string }; status?: number } };

      // Handle max attempts reached
      if (error.response?.status === 400 && error.response?.data?.error?.includes('Maximum number of attempts')) {
        setError('You have already completed this quiz and cannot take it again.');
        setShowStartDialog(false);
      } else {
        setError(error.response?.data?.error || error.response?.data?.message || 'Failed to start quiz');
      }
      setLoading(false);
    }
  };

  const handleSelectOption = (optionId: number) => {
    if (showFeedback) return;
    setSelectedOptionId(optionId);
  };

  const triggerConfetti = () => {
    const duration = 2000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);
  };

  const triggerCompletionCelebration = () => {
    // Big celebration for completing the quiz
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 7,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.8 },
        colors: ['#FFD700', '#FFA500', '#FF8C00', '#4CAF50'],
      });
      confetti({
        particleCount: 7,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.8 },
        colors: ['#FFD700', '#FFA500', '#FF8C00', '#4CAF50'],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  };

  const handleSubmitAnswer = async () => {
    if (!selectedOptionId || !attempt || !quiz) return;

    try {
      setSubmitting(true);
      const currentQuestion = quiz.questions[currentQuestionIndex];

      // Calculate response time in seconds
      const responseTimeSeconds = Math.floor((Date.now() - questionStartTime) / 1000);

      console.log('[handleSubmitAnswer] Question:', currentQuestionIndex + 1);
      console.log('[handleSubmitAnswer] Response time:', responseTimeSeconds, 'seconds');
      console.log('[handleSubmitAnswer] Start time:', questionStartTime);
      console.log('[handleSubmitAnswer] Current time:', Date.now());
      console.log('[handleSubmitAnswer] Time diff (ms):', Date.now() - questionStartTime);

      const response = await submitAnswer(
        quizId,
        attempt.id,
        currentQuestion.id,
        selectedOptionId,
        responseTimeSeconds
      );

      setIsCorrect(response.isCorrect);
      setPointsEarned(response.pointsEarned || 0);
      setShowFeedback(true);

      // Trigger confetti if correct
      if (response.isCorrect) {
        triggerConfetti();
      }

      // Wait 2 seconds to show feedback, then move to next question
      setTimeout(() => {
        if (currentQuestionIndex < quiz.questions.length - 1) {
          // Move to next question
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          setSelectedOptionId(null);
          setShowFeedback(false);
          setSubmitting(false);
          // CRITICAL: Reset question start time synchronously to prevent timing bugs
          setQuestionStartTime(Date.now());
        } else {
          // Quiz complete - trigger celebration animation
          triggerCompletionCelebration();
          // Wait a bit for the celebration, then complete
          setTimeout(() => {
            handleCompleteQuiz();
          }, 1500);
        }
      }, 2000);
    } catch (err: unknown) {
      console.error('Error submitting answer:', err);
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to submit answer');
      setSubmitting(false);
    }
  };

  const handleCompleteQuiz = async () => {
    if (!attempt) return;

    try {
      await completeQuizAttempt(quizId, attempt.id);
      navigate(`/student/quizzes/${quizId}/results`);
    } catch (err: unknown) {
      console.error('Error completing quiz:', err);
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to complete quiz');
    }
  };

  const handleTimeExpired = async () => {
    if (!attempt || !quiz) return;

    // Auto-submit current answer if selected
    if (selectedOptionId && !showFeedback) {
      await handleSubmitAnswer();
    } else {
      // Skip to next question or complete quiz
      if (currentQuestionIndex < quiz.questions.length - 1) {
        // Move to next question without submitting
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setSelectedOptionId(null);
        // Reset question start time for the next question
        setQuestionStartTime(Date.now());
      } else {
        // Last question, complete quiz
        await handleCompleteQuiz();
      }
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={() => navigate('/student/quizzes')} sx={{ mt: 2 }}>
            Back to Quizzes
          </Button>
        </Container>
      </MainLayout>
    );
  }

  if (!quiz) {
    return (
      <MainLayout>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="info">Loading quiz...</Alert>
        </Container>
      </MainLayout>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];

  return (
    <MainLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Start Quiz Dialog */}
        <Dialog open={showStartDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Start Quiz: {quiz.title}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              Once you start, each question will have its own timer. Answer quickly to earn bonus points!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • {quiz.questions.length} questions<br />
              • Each question has a time limit<br />
              • Faster correct answers earn more points<br />
              • You cannot pause or retake this quiz<br />
              • Make sure you have a stable internet connection
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => navigate('/student/quizzes')} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleStartQuiz} variant="contained" color="primary" size="large">
              Start Quiz
            </Button>
          </DialogActions>
        </Dialog>

        {/* Quiz Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {quiz.title}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <QuizProgressBar
                current={currentQuestionIndex + 1}
                total={quiz.questions.length}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {questionTimeRemaining > 0 && (
                <CountdownTimer
                  key={currentQuestionIndex}
                  totalSeconds={questionTimeRemaining}
                  onExpire={handleTimeExpired}
                  isPaused={showFeedback}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Question Section */}
        <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {currentQuestion.questionText}
          </Typography>
        </Paper>

        {/* Answer Options */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {currentQuestion.options?.map((option, index) => (
            <Grid item xs={12} sm={6} key={option.id}>
              <AnswerOptionButton
                option={option}
                optionIndex={index}
                selected={selectedOptionId === option.id}
                onClick={() => handleSelectOption(option.id)}
                disabled={showFeedback || submitting}
                showFeedback={showFeedback}
                isCorrect={isCorrect}
              />
            </Grid>
          ))}
        </Grid>

        {/* Submit Button */}
        {!showFeedback && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmitAnswer}
              disabled={!selectedOptionId || submitting}
              sx={{ minWidth: 200, py: 1.5 }}
            >
              {submitting ? 'Submitting...' : 'Submit Answer'}
            </Button>
          </Box>
        )}

        {/* Feedback */}
        <AnimatePresence>
          {showFeedback && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{
                scale: 1,
                opacity: 1,
                x: isCorrect ? 0 : [-10, 10, -10, 10, -5, 5, 0],
              }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: isCorrect ? 0.3 : 0.5 }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  bgcolor: isCorrect ? 'success.light' : 'error.light',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold" color={isCorrect ? 'success.dark' : 'error.dark'}>
                  {isCorrect ? '🎉 Correct!' : '😔 Incorrect'}
                </Typography>
                {isCorrect && (
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  >
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color="success.dark"
                      sx={{ mt: 1, mb: 1 }}
                    >
                      +{pointsEarned} points!
                    </Typography>
                  </motion.div>
                )}
                {!isCorrect && (
                  <Typography variant="h5" fontWeight="bold" color="error.dark" sx={{ mt: 1, mb: 1 }}>
                    0 points
                  </Typography>
                )}
                <Typography variant="body1" color={isCorrect ? 'success.dark' : 'error.dark'}>
                  {currentQuestionIndex < quiz.questions.length - 1
                    ? 'Moving to next question...'
                    : 'Completing quiz...'}
                </Typography>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </MainLayout>
  );
}

export default QuizPlayPage;
